import {useMutation} from '@tanstack/react-query';
import React, {ReactElement, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {useDebouncedCallback} from 'use-debounce';
import {useDispatch, useSelector} from 'react-redux';
import {type TypedTranslation} from '../../../../i18n';
import {IElementStaticText} from '../../../../incase-domain/elements/staticText';
import {generateText} from '../../../../service/languageModel.service';
import TextArea from '../../../../ui/components/TextArea';
import {TABS} from '../../form/part/tabs';
import BorderRadiusChange from '../common/borderRadiusChange';
import ElementShadow from '../common/elementShadow';
import FontAndBackgroundColorPicker, {COLOR_FIELDS} from '../common/fontAndBackgroundColorPicker';
import SlideChange from '../common/slideChange';
import AnimationForm from '../common/animationForm';
import SectionTitle from '../../../elements/common/SectionTitle';
import Tiptap from '../../../elements/tipTap/TipTap';
import Button from '../../../../incase-domain/components/button/Button';
import {AppState} from '../../../../store/reducer';
import {setDebounceChangeHandler} from '../../../../store/actions/tiptapEditor.actions';
import {editorSelector} from '../../../../store/selectors/tiptapEditor.selectors';
import {useHighlightedElement, useSetHighlightedElement} from '../../../../utils/hooks/slideUpdates/useSetSlideElement';

interface HighlightedStaticTextElementComponentProps {
    overrideHighlightedElement?: IElementStaticText;
    overrideHighlightedElementChangeHandler?: {single: (field: keyof IElementStaticText | string, value: unknown) => void};
    isFlipCard?: boolean;
    activeTab: string;
}

HighlightedStaticTextElementComponent.defaultProps = {
    isFlipCard: false,
};

function HighlightedStaticTextElementComponent(props: HighlightedStaticTextElementComponentProps): ReactElement {
    const {overrideHighlightedElement, overrideHighlightedElementChangeHandler, isFlipCard, activeTab} = props;
    const updateHighlightedElement = useSetHighlightedElement();
    const highlightedElement = useHighlightedElement() as IElementStaticText;
    const element = overrideHighlightedElement || highlightedElement;
    const {id, text} = element;
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const changeHandler = overrideHighlightedElementChangeHandler || updateHighlightedElement;

    const {mutate, isLoading, isError, error} = useMutation(generateText);

    const debouncedChangeHandler = useDebouncedCallback((e) => changeHandler.single('text', e), 250);

    const editor = useSelector((state: AppState) => editorSelector(state, id));

    useEffect(() => {
        if (editor) {
            dispatch(setDebounceChangeHandler(id, debouncedChangeHandler));
        }
    }, [editor, dispatch, debouncedChangeHandler, id]);

    const [gptPrompt, setGptPrompt] = React.useState('');

    async function handleGenerateText(): Promise<void> {
        const prompt = {
            text,
            prompt: gptPrompt,
        };
        mutate(
            {prompt},
            {
                onSuccess: (data) => {
                    if (data.length > 0) {
                        changeHandler.single('text', data);
                        setGptPrompt('');
                    }
                },
            }
        );
    }

    function appendText(text: string): void {
        editor?.chain().focus('end').createParagraphNear().insertContent(text).run();
    }

    if (activeTab === TABS.STYLE.id) {
        return <ElementShadow highlightedElement={element} highlightedElementChangeHandler={changeHandler} />;
    }

    if (activeTab === TABS.ADVANCED.id) {
        return (
            <>
                <AnimationForm
                    appearAfter={element.appearAfter || 0}
                    elementAnimations={element.elementAnimations}
                    highlightedElementChangeHandler={changeHandler}
                />
                {!isFlipCard && (
                    <div className="l-group--form">
                        <SlideChange
                            slideChange={element.slideChange}
                            setSlideChange={(slideChange) => {
                                changeHandler.single('slideChange', slideChange);
                            }}
                        />
                    </div>
                )}
            </>
        );
    }

    return (
        <>
            <div className="l-group--form">
                <SectionTitle label={t('designSidepanel.gpt.smartAutofill')} infoText={t('designSidepanel.gpt.textTooltip')} />
                <div className="u-flex u-flex-col u-v-end u-gap-2">
                    <TextArea placeholder={t('designSidepanel.gpt.placeholder')} onChange={(e) => setGptPrompt(e)} value={gptPrompt} />
                    <div className="u-self-bottom">
                        <Button
                            variant="soft"
                            color="secondary"
                            size="sm"
                            loading={isLoading}
                            leading="solar:rocket-2-linear"
                            onClick={handleGenerateText}
                        >
                            {t('designSidepanel.gpt.generate')}
                        </Button>
                    </div>
                </div>

                {isError && <div style={{fontSize: '0.8em'}}>{t((error as {message: TypedTranslation}).message)}</div>}
            </div>

            <div className="l-group--form">
                <SectionTitle label={t('designSidepanel.variablesTitle')} infoText={t('designSidepanel.variablesDescription')} />
                <div className="u-flex flex-wrap u-gap-1">
                    <Button
                        variant="soft"
                        leading="ph:plus"
                        color="secondary"
                        size="xs"
                        onClick={() => {
                            appendText('{{score}}');
                        }}
                    >
                        {t('designSidepanel.variablePointsScored')}
                    </Button>
                    <Button
                        variant="soft"
                        leading="ph:plus"
                        color="secondary"
                        size="xs"
                        onClick={() => {
                            appendText('{{timeElapsed}}');
                        }}
                    >
                        {t('designSidepanel.variableTimeElapsed')}
                    </Button>
                </div>
            </div>

            <div className="l-group--form">
                <SectionTitle label={t('designSidepanel.style')} />
                <div className=" u-flex u-flex-col u-gap-3">
                    <Tiptap id={id} />

                    <FontAndBackgroundColorPicker
                        highlightedElement={element}
                        highlightedElementChangeHandler={changeHandler}
                        colorFields={[COLOR_FIELDS.BACKGROUND_COLOR]}
                    />

                    <BorderRadiusChange
                        borderRadius={element.borderRadius}
                        highlightedElementChangeHandler={(value) => changeHandler.single('borderRadius', value)}
                    />
                </div>
            </div>
        </>
    );
}

export default HighlightedStaticTextElementComponent;
